import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from "@angular/core";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { FormControl } from "@angular/forms";
import { combineLatest, Observable, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import {
  instanceOfDialogInfo,
  instanceOfExtendedDialogInfo,
  DialogInfo,
  ExtendedDialogInfo
} from "@onsip/common/interfaces/presentity";
import { WebCallTopicService } from "@onsip/common/services/api/resources/webCallTopic/web-call-topic.service";
import { CallStoreHourlyService } from "@onsip/common/services/sayso/call-store-hourly.service";
import { CallersPresentService } from "@onsip/common/services/sayso/callers-present.service";
import { UserAvailability } from "@onsip/common/services/sayso/firestore-auth-user.service";
import { SubscriptionControllerService } from "@onsip/common/services/subscription-controller.service";
import { UsersStoreSubscriptionService } from "../../shared/services/userStoreSubscription/users-store-subscription.service";
import { MatTableDataSource } from "@angular/material/table";

/** Custom object combining some basic user info, global availability and user presence data */
export interface CombinedUserAvailability {
  aor: string;
  availability: UserAvailability;
  avatar: string;
  busyTimestamp: number | undefined;
  id: string;
  isBusy: boolean;
  name: string;
}
/** Master table data object */
export interface ManagerDashboardTableData {
  "topic-name": string;
  "agent-info": Array<ManagerDashboardAgentData>;
  potential: number;
  "on-call": number;
  total: number;
  answered: number;
  cancelled: number;
  rejected: number;
}
/** Expandable agent data object */
export interface ManagerDashboardAgentData {
  id: string;
  avatar: string;
  callStart: number | undefined;
  callStatus: UserAvailability | "busy";
  name: string;
}

/** Rep primary sort order */
const statusSortPriority = {
  available: 0,
  busy: 1,
  unavailable: 2,
  undefined: 3
};
@Component({
  templateUrl: "./manager-dashboard.component.html",
  styleUrls: ["./manager-dashboard.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ManagerDashboardComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;

  /** Master data source */
  dataSource = new MatTableDataSource<ManagerDashboardTableData>([]);
  /** subset of topic names that are expanded */
  expandedElements: Array<string> = [];
  /** Flag for mobile few */
  isMobile: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.XSmall)
    .pipe(map(result => result.matches));
  /** Column list, used for some table attrs */
  displayedColumns: Array<string> = [
    "topic-name",
    "potential",
    "on-call",
    "total",
    "answered",
    "cancelled",
    "rejected"
  ];
  /** FormControl for header search */
  search = new FormControl("", { nonNullable: true });
  private managerInfo!: Array<ManagerDashboardTableData>;
  private showMobile = false;
  private unsubscriber = new Subscription();

  constructor(
    private breakpointObserver: BreakpointObserver,
    private callersPresentService: CallersPresentService,
    private callStoreHourlyService: CallStoreHourlyService,
    private webCallTopicService: WebCallTopicService,
    private subscriptionControllerService: SubscriptionControllerService,
    private usersStoreSubscriptionService: UsersStoreSubscriptionService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  ngOnInit() {
    // Answered, cancelled, rejected, total call data from firestore
    const callStore = this.callStoreHourlyService.state.pipe(
      map(state =>
        state
          .filter(call => Object.keys(call.sessions).length > 0) // filter if no sessions
          .filter(call =>
            Object.keys(call.sessions)
              .map(key => call.sessions[key])
              .every(session => session.endedAt !== undefined)
          ) // filter no ended sessions
          .map(call => {
            const sessions = Object.keys(call.sessions).map(key => call.sessions[key]);
            const answered = sessions.some(session => session.connectedAt !== undefined); // answered if any session has connectedAt
            const latestEndReason = sessions.sort(
              (b, a) => (a.endedAt ? a.endedAt : 0) - (b.endedAt ? b.endedAt : 0)
            )[0].endReason;
            return {
              target: call.target,
              answered: answered ? 1 : 0,
              cancelled: answered ? 0 : latestEndReason === "cancelled" ? 1 : 0,
              rejected: answered ? 0 : latestEndReason === "failed" ? 1 : 0,
              total:
                answered || latestEndReason === "cancelled" || latestEndReason === "failed" ? 1 : 0 // ensure the other 3 add up
            };
          })
      )
    );

    // All users in at least one topics with their associated global availability and blf presence data
    const users = combineLatest({
      userIds: this.webCallTopicService.getAllRepUserIds(),
      // this.configurationService.getAllRepUserIds(),
      presenceState: this.subscriptionControllerService.state,
      usersStoreState: this.usersStoreSubscriptionService.state
    }).pipe(
      map(
        ({ userIds, presenceState, usersStoreState }) =>
          userIds
            .map(id => {
              const usersStoreData = usersStoreState.find(user => user.id === id);
              // Look up presence data based off aor from UserState
              const presenceData = presenceState.presentity
                .filter(p => p.event === "dialog")
                .find(pres => pres.aor === usersStoreData?.aor);
              if (
                usersStoreData &&
                presenceData &&
                (instanceOfDialogInfo(presenceData.eventData) ||
                  instanceOfExtendedDialogInfo(presenceData.eventData))
              ) {
                return {
                  aor: presenceData.aor,
                  avatar: usersStoreData.picture,
                  availability: usersStoreData.availability,
                  busyTimestamp: this.getBusyConfirmedTime(presenceData.eventData),
                  id: usersStoreData.id,
                  isBusy: this.getBusyState(presenceData.eventData),
                  name: usersStoreData.name
                };
              }
            })
            .filter(
              (combinedUserData): combinedUserData is CombinedUserAvailability => !!combinedUserData
            ) // typeguard, filter out "undefined" values from above
      )
    );

    // Compile master table data observable
    const tableData: Observable<Array<ManagerDashboardTableData>> = combineLatest({
      webCallTopics: this.webCallTopicService.orgState,
      usersState: users,
      callStoreState: callStore,
      potentialState: this.callersPresentService.state
    }).pipe(
      map(({ webCallTopics, usersState, callStoreState, potentialState }) =>
        webCallTopics.length
          ? webCallTopics
              .map(webTopic => ({
                "topic-name": webTopic.configuration?.targetDisplay || "",
                "agent-info": webTopic.webCallTopicReps
                  .map(
                    topicRep =>
                      usersState.find(user => user && user.id === topicRep.repId.toString()) // match on id
                  )
                  .map(
                    agent =>
                      agent && {
                        id: agent.id,
                        avatar: agent.avatar,
                        callStart: agent.busyTimestamp,
                        callStatus: agent.isBusy ? "busy" : agent.availability,
                        name: agent.name
                      }
                  )
                  .filter((agent): agent is ManagerDashboardAgentData => !!agent) // filter from list if agent not found
                  .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                  .sort(
                    (a, b) => statusSortPriority[a.callStatus] - statusSortPriority[b.callStatus]
                  ),
                potential: potentialState.filter(pot => pot.tid === webTopic.name).length,
                "on-call": usersState.filter(
                  user =>
                    webTopic.webCallTopicReps.find(rep => rep.repId.toString() === user?.id) &&
                    user?.isBusy
                ).length,
                total: callStoreState
                  .filter(state => state.target === webTopic.name)
                  .reduce((acc, curr) => acc + curr.total, 0),
                answered: callStoreState
                  .filter(state => state.target === webTopic.name)
                  .reduce((acc, curr) => acc + curr.answered, 0),
                cancelled: callStoreState
                  .filter(state => state.target === webTopic.name)
                  .reduce((acc, curr) => acc + curr.cancelled, 0),
                rejected: callStoreState
                  .filter(state => state.target === webTopic.name)
                  .reduce((acc, curr) => acc + curr.rejected, 0)
              }))
              .sort((a, b) =>
                a["topic-name"].toLowerCase() > b["topic-name"].toLowerCase() ? 1 : -1
              )
          : []
      )
    );

    this.unsubscriber.add(
      tableData
        .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(state => {
          // added a distinctUntilChanged because tableData is very noisy but doesnt really change the data itself
          this.dataSource.data = state;
          this.managerInfo = state;
          this.cdRef.markForCheck();
        })
    );

    this.unsubscriber.add(this.search.valueChanges.subscribe(() => this.applyFilter()));
    this.unsubscriber.add(
      this.isMobile.subscribe(result => {
        this.showMobile = result;
        this.dataSource.data = this.managerInfo;
      })
    );
  }

  applyFilter() {
    this.dataSource.filter = this.search.value.toLowerCase();
  }

  /** Used to assist angular change detection with rows in table (topics) */
  trackByTopicName(idx: number, item: ManagerDashboardTableData) {
    return item["topic-name"];
  }

  /** Used in ngFor's [trackBy function](https://angular.io/api/common/NgForOf#ngForTrackBy) to track changes to user data
   *  This is important to avoid "flashing" avatars i.e. destroying on changes insides of ngFor loop unnessesarily
   */
  trackByUserId(idx: number, item: ManagerDashboardAgentData) {
    return item.id;
  }

  showMobileRow(): boolean {
    return this.showMobile;
  }

  showDesktopRow(): boolean {
    return !this.showMobile;
  }

  private getBusyState(dialogInfo: DialogInfo | ExtendedDialogInfo): boolean {
    if (instanceOfDialogInfo(dialogInfo)) {
      return dialogInfo.priority > 0;
    } else if (instanceOfExtendedDialogInfo(dialogInfo)) {
      return dialogInfo.dialogs.some(d => d.priority > 0);
    } else {
      throw new Error("getBusyState only allowed for user dialogInfo");
    }
  }

  private getBusyConfirmedTime(dialogInfo: DialogInfo | ExtendedDialogInfo): number | undefined {
    if (instanceOfDialogInfo(dialogInfo)) {
      return dialogInfo.confirmedTime;
    } else if (instanceOfExtendedDialogInfo(dialogInfo)) {
      const sorted = dialogInfo.dialogs
        .filter(dialog => dialog.priority > 0)
        .sort((a, b) => a.confirmedTime - b.confirmedTime);
      if (sorted.length) return sorted[0].confirmedTime;
    } else {
      throw new Error("getBusyConfirmedTime only allowed for user dialogInfo");
    }
  }
}
